import { useMemo } from 'react'

import { joiResolver } from '@hookform/resolvers/joi'
import { Box, Button, Dialog, DialogActions, DialogContent, Grid, Typography } from '@mui/material'
import { SubmitHandler, useForm } from 'react-hook-form'

import { ControlledSelector, ControlledTextField, ISelectorOptions } from 'components/common'
import { FplDialogTitle } from 'components/common/Dialog'
import { JoiValidationOptions } from 'constants/index'
import { CancellationOrLostDtoInput, useGetCancellationOrLostReasonsQuery } from 'generated/graphql'
import { usePermission } from 'providers'
import { ConfirmCancellationOrLostDialogValidation } from 'validation-schemas'

interface IProps {
  bookingId: string | undefined
  open: boolean
  isQuote: boolean
  setOpen: any
  onConfirmCancellation: (input: CancellationOrLostDtoInput) => void
  onConfirmQuoteLost: (input: CancellationOrLostDtoInput) => void
}

type CancellationOrLostForm = Omit<CancellationOrLostDtoInput, 'bookingId'>

const ConfirmCancellationOrLostDialog = (props: IProps) => {
  const { open, setOpen, onConfirmCancellation, onConfirmQuoteLost, isQuote, bookingId } = props

  const {
    control,
    handleSubmit,
    formState: { errors },
  } = useForm<CancellationOrLostForm>({
    resolver: joiResolver(ConfirmCancellationOrLostDialogValidation, JoiValidationOptions),
    defaultValues: {
      cancellationOrLostReasonId: '',
      cancellationOrLostReasonDetails: '',
    },
  })

  const [isAllowedToViewQuoteLostReasonList] = usePermission('ViewQuoteLostReasonList')

  const { data: cancellationReasonsData } = useGetCancellationOrLostReasonsQuery({
    skip: !isAllowedToViewQuoteLostReasonList,
  })

  const quoteLostReasonOptions: ISelectorOptions = useMemo(
    () =>
      cancellationReasonsData?.cancellationOrLostReasons?.map((quoteLostReason) => ({
        value: quoteLostReason.id,
        label: quoteLostReason.name,
      })) || [],
    [cancellationReasonsData],
  )

  const handleClose = () => {
    setOpen(false)
  }

  const handleConfirm: SubmitHandler<CancellationOrLostForm> = (data) => {
    if (!bookingId) return

    const { cancellationOrLostReasonId, cancellationOrLostReasonDetails } = data

    if (isQuote) {
      onConfirmQuoteLost({
        bookingId,
        cancellationOrLostReasonId,
        cancellationOrLostReasonDetails,
      })
      return
    }

    onConfirmCancellation({
      bookingId,
      cancellationOrLostReasonId,
      cancellationOrLostReasonDetails,
    })
    setOpen(false)
  }

  return (
    <Dialog
      maxWidth='xs'
      fullWidth
      open={open}
      onClose={handleClose}
      aria-labelledby='confirm-cancellation-or-lost-dialog'>
      <FplDialogTitle id='confirm-cancellation-or-lost-dialog' onClose={handleClose}>
        <Typography variant='h6'>Confirm Cancellation or Lost</Typography>
      </FplDialogTitle>
      <DialogContent>
        <Box pt={1}>
          <Grid container spacing={2}>
            <Grid item xs={12}>
              <ControlledSelector
                control={control}
                name='cancellationOrLostReasonId'
                label='Cancellation Or Lost Reason'
                required
                options={quoteLostReasonOptions}
                error={!!errors.cancellationOrLostReasonId}
                helperText={errors.cancellationOrLostReasonId?.message}
              />
            </Grid>
            <Grid item xs={12}>
              <ControlledTextField
                control={control}
                name='cancellationOrLostReasonDetails'
                label='Cancellation Or Lost Reason Details'
                multiline
                rows={3}
                error={!!errors.cancellationOrLostReasonDetails}
                helperText={errors.cancellationOrLostReasonDetails?.message}
              />
            </Grid>
          </Grid>
        </Box>
      </DialogContent>
      <DialogActions>
        <Button variant='contained' color='grey' onClick={handleClose}>
          Cancel
        </Button>
        <Button variant='contained' onClick={handleSubmit(handleConfirm)}>
          Ok
        </Button>
      </DialogActions>
    </Dialog>
  )
}

export default ConfirmCancellationOrLostDialog
